import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { getArea, AREAS } from "@/lib/areas";
import { SERVICES, BRAND } from "@/lib/content";
import { getEpcTown } from "@/lib/epc";
import { getCsTown } from "@/lib/conditionSurveys";

export default function AreaPage() {
  const { slug } = useParams();
  const area = getArea(slug);
  if (!area) return <Navigate to="/areas" replace />;

  const services = area.popularServices
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);
  const nearby = area.nearby.map((n) => AREAS.find((a) => a.slug === n)).filter(Boolean);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: `Property Surveys & EPCs in ${area.name}`,
        provider: {
          "@type": "ProfessionalService",
          name: "Declan Bishop — Property Consultant",
          url: "https://dbpropertyconsultant.co.uk",
          telephone: "+44 7947 981 727",
        },
        areaServed: { "@type": "City", name: area.name },
        description: area.metaDescription,
        url: `https://dbpropertyconsultant.co.uk/areas/${area.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://dbpropertyconsultant.co.uk/" },
          { "@type": "ListItem", position: 2, name: "Areas", item: "https://dbpropertyconsultant.co.uk/areas" },
          { "@type": "ListItem", position: 3, name: area.name, item: `https://dbpropertyconsultant.co.uk/areas/${area.slug}` },
        ],
      },
    ],
  };

  return (
    <SiteLayout
      pageTitle={`Property Surveyor in ${area.name}`}
      pageDescription={area.metaDescription}
      pagePath={`/areas/${area.slug}`}
    >
      <JsonLd id={`area-schema-${area.slug}`} data={schema} />

      <section data-testid="area-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-14 pb-16 md:pt-20 md:pb-20">
          <nav data-testid="area-breadcrumb" className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-brand-navy/55">
            <Link to="/" className="hover:text-brand-navy">Home</Link>
            <ChevronRight size={12} />
            <Link to="/areas" className="hover:text-brand-navy">Areas</Link>
            <ChevronRight size={12} />
            <span className="text-brand-navy">{area.name}</span>
          </nav>
          <div className="overline mt-10" data-testid="area-overline">{area.county} · Local area guide</div>
          <h1
            data-testid="area-headline"
            className="mt-6 font-serif text-[42px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            Property surveys &amp; EPCs in{" "}
            <span className="italic font-light text-brand-champagne">{area.name}.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            {area.intro}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="area-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Request a Quote in {area.name}
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="area-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section data-testid="area-local-knowledge" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-12 gap-y-10 md:gap-x-12">
            <div className="col-span-12 md:col-span-4">
              <div className="overline">Local knowledge</div>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                Understanding {area.name}&apos;s property stock.
              </h2>
              <div className="mt-6 space-y-2 text-sm leading-relaxed">
                {getEpcTown(area.slug) && (
                  <p>
                    <Link to={`/epc/${area.slug}`} className="ink-link font-medium text-brand-navy" data-testid="area-epc-link">
                      EPCs in {area.name} from £85 →
                    </Link>
                  </p>
                )}
                {getCsTown(area.slug) && (
                  <p>
                    <Link to={`/condition-surveys/${area.slug}`} className="ink-link font-medium text-brand-navy" data-testid="area-cs-link">
                      Condition surveys in {area.name} →
                    </Link>
                  </p>
                )}
              </div>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 text-base md:text-lg text-brand-navy/75 leading-relaxed">
              {area.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="area-services" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Most requested in {area.name}</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight">
            Popular services locally.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services#${s.slug}`}
                data-testid={`area-service-${s.slug}`}
                className="group bg-brand-cream p-8 md:p-10 hover:bg-white transition-colors"
              >
                <div className="font-serif text-3xl text-brand-champagne">{s.number}</div>
                <h3 className="mt-4 font-serif text-2xl text-brand-navy">{s.title}</h3>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link">
                  View service
                  <ArrowUpRight size={13} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="area-nearby" className="bg-brand-navy text-brand-cream">
        <div className="container-narrow py-16 md:py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              Also covering nearby
            </div>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/areas/${n.slug}`}
                  data-testid={`area-nearby-${n.slug}`}
                  className="font-serif text-2xl md:text-3xl hover:text-brand-champagne transition-colors"
                >
                  {n.name}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/areas"
            data-testid="area-all-areas-link"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-champagne hover:text-brand-cream transition-colors whitespace-nowrap"
          >
            All areas covered
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
